import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, Loader2, AlertCircle, Globe } from 'lucide-react';
import { PricingCard } from './PricingCard';
import { AddOnCard } from './AddOnCard';
import { PricingFAQ } from './PricingFAQ';
import { withSectionTracking } from '../../lib/withSectionTracking';
import { useButtonTracking } from '../../lib/useAnalyticsTracking';
import subscriptionService, { SubscriptionPlan } from '../../services/subscriptionService';
import { Button } from '../common/Button';

function PricingComponent() {
  const [animationPhase, setAnimationPhase] = useState(0);
  const sectionRef = useRef<HTMLElement | null>(null);
  
  // Data loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [addOns, setAddOns] = useState<SubscriptionPlan[]>([]);
  // Add a dataLoaded state to trigger re-renders
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Geographical pricing states
  const [shouldShowPricing, setShouldShowPricing] = useState(true);
  const [userLocation, setUserLocation] = useState<{ country: string; countryCode: string } | null>(null);
  const [locationLoading, setLocationLoading] = useState(true); 
  
  // Keep track of component mount state
  const isMounted = useRef(true);

  // Set up intersection observer for animations
  useEffect(() => {
    setTimeout(() => setAnimationPhase(1), 2200); // Cards

  }, []);

  // Fetch subscription plans from API
  const fetchPlans = useCallback(async () => {
    if (!isMounted.current) return;
    
    try {
      setLoading(true);
      
      // Fetch all plans
      const allPlans = await subscriptionService.getSubscriptionPlans(true);
      
      if (!isMounted.current) return;
      
      console.log('Fetched plans:', allPlans);
      
      // Filter plans by isAddon flag
      const regularPlans = allPlans.filter(plan => !plan.isAddon);
      const addonPlans = allPlans.filter(plan => plan.isAddon);
      
      console.log('Regular plans:', regularPlans);
      console.log('Add-on plans:', addonPlans);
      
      // Update state with plans
      setPlans(regularPlans);
      setAddOns(addonPlans);
      setError(null);
      
      // Force a re-render by setting dataLoaded to true
      setDataLoaded(true);
    } catch (err) {
      console.error('Error fetching subscription plans:', err);
      if (isMounted.current) {
        setError('Failed to load subscription plans. Please try again later.');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Set mounted ref
    isMounted.current = true;
    
    // Fetch plans
    fetchPlans();
    
    // Cleanup
    return () => {
      isMounted.current = false;
    };
  }, [fetchPlans]);

  // Detect user location on component mount
  useEffect(() => {
    const checkLocation = async () => {
      try {
        setLocationLoading(true);
        const location = await detectUserLocation();
        
        if (isMounted.current) {
          setShouldShowPricing(location.isIndia);
          setUserLocation({ country: location.country, countryCode: location.countryCode });
          
          console.log('Location detected:', location, 'Show pricing:', location.isIndia);
        }
      } catch (error) {
        console.error('Error detecting location:', error);
        if (isMounted.current) {
          // Fallback to showing pricing (India default)
          setShouldShowPricing(true);
          setUserLocation({ country: 'India', countryCode: 'IN' });
        }
      } finally {
        if (isMounted.current) {
          setLocationLoading(false);
        }
      }
    };

    checkLocation();
  }, []);

  // Hook for tracking button clicks in the Pricing section
  const trackButtonClick = useButtonTracking('Pricing Section');

  const handleGetFreeInterview = () => {
    trackButtonClick('Get 1 Free Interview');
    // Navigate to subscription page with UTM parameters
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    window.open(`${platformUrl}/subscriptions?utm_source=landing_page&utm_medium=cta_button&utm_campaign=free_interview`, '_blank');
  };



// Helper function to calculate approximate number of interviews based on minutes
const calculateInterviews = (minutes: number, isUnlimited: boolean): string => {
  if (isUnlimited) {
    return '∞';
  }
  // Assuming an average interview is about 15-20 minutes
  const minInterviews = Math.floor(minutes / 18);
  const maxInterviews = Math.ceil(minutes / 15);
  
  return minInterviews === maxInterviews 
    ? `${minInterviews}` 
    : `${minInterviews}-${maxInterviews}`;
};
  // Helper function to format features for pricing cards
  const formatPlanFeatures = (plan: SubscriptionPlan) => {
    // Create a minutes feature about interview minutes if not included
    const minutesFeature = {
      text: plan.isUnlimited
        ? 'Unlimited AI Interview Practice & Feedback'
        : `${plan.interviewMinutes} minutes (${calculateInterviews(plan.interviewMinutes, plan.isUnlimited)}) of AI Interview Practice & Feedback`,
      available: true,
      highlight: false
    };
    
    // Map the rest of the features
    const formattedFeatures = plan.features.map(feature => ({
      text: feature,
      available: true,
      highlight: false
    }));
    
    return [minutesFeature, ...formattedFeatures];
  };

  // Helper function to format features for add-on cards
  const formatAddOnFeatures = (addon: SubscriptionPlan) => {
    const features = [...addon.features];
    
    // Add interview minutes feature if not already included
    if (!features.some(feature => feature.includes('minutes'))) {
      features.unshift(`${addon.interviewMinutes} minutes of additional practice`);
    }
    
    // Add validity feature if not already included
    if (!features.some(feature => feature.toLowerCase().includes('valid'))) {
      features.push('Valid till Plan Expiry');
    }
    
    return features;
  };

  // Debug rendering
  console.log('Rendering Pricing component with:', {
    loading,
    plansLength: plans.length,
    addOnsLength: addOns.length,
    error,
    dataLoaded
  });

  // If loading, show a loading state
  if (loading || locationLoading) {
    return (
      <section id="pricing" className={`py-16 relative overflow-hidden ${animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
        <div className="text-base flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 text-primary-light dark:text-primary-dark animate-spin mb-4" />
          <p className="text-text-secondary-light dark:text-text-secondary-dark">
            {loading ? 'Loading subscription plans...' : 'Detecting your location...'}
          </p>
        </div>
      </section>
    );
  }

  // If there was an error, show an error message
  if (error) {
    return (
      <section id="pricing" className="flex flex-col items-center justify-center py-16 relative overflow-hidden ">
        <div className="p-6 rounded-xl bg-red-500/10 text-center w-full md:w-1/2">
          <p className="text-red-500 dark:text-red-400">{error}</p>
          <button 
            className="btn-secondary mt-4"
            onClick={() => fetchPlans()}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  // Simple location detection function
  const detectUserLocation = async (): Promise<{ country: string; countryCode: string; isIndia: boolean }> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch('https://ipapi.co/json/', {
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        return {
          country: data.country_name || 'India',
          countryCode: data.country_code || 'IN',
          isIndia: data.country_code === 'IN'
        };
      }
    } catch (error) {
      console.warn('Failed to detect user location:', error);
    }

    // Fallback to India
    return {
      country: 'India',
      countryCode: 'IN',
      isIndia: true
    };
  };

  // Handle trial request for non-Indian users
  const handleTrialRequest = (planId: string, planName: string, planType: string) => {
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    const signupUrl = `${platformUrl}/signup?utm_source=landing_page&utm_medium=pricing_card&utm_campaign=trial_request&utm_content=${planType}&plan_trial=${planId}`;
    
    trackButtonClick(`Request ${planName} Trial`);
    window.open(signupUrl, '_blank');
  };

  // Handle trial request for add-ons
  const handleAddonTrialRequest = (planId: string, planName: string) => {
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    const signupUrl = `${platformUrl}/signup?utm_source=landing_page&utm_medium=addon_card&utm_campaign=addon_trial&utm_content=${planName.toLowerCase()}&addon_trial=${planId}`;
    
    trackButtonClick(`Request ${planName} Add-on Trial`);
    window.open(signupUrl, '_blank');
  };

  return (
    <section 
      ref={sectionRef}
      id="pricing" 
      className="py-8 relative overflow-hidden"
    >
      <div className="container mx-auto relative">
        {/* Location indicator for non-Indian users */}
        {!shouldShowPricing && userLocation && (
          <div className="mb-8 p-6 rounded-xl border border-blue-500/20 dark:border-blue-400/20 bg-blue-500/5 dark:bg-blue-400/5 backdrop-blur-sm max-w-4xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-500/10 dark:bg-blue-400/10">
                <Globe className="w-6 h-6 text-blue-500 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                  Welcome from {userLocation.country}! 🌍
                </h3>
                <p className="text-text-secondary-light dark:text-text-secondary-dark">
                  MockTrail currently supports INR pricing for India. We're expanding globally soon! 
                  <span className="font-medium text-text-primary-light dark:text-text-primary-dark"> Sign up now to request trial.</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Plans */}
        <div 
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto mb-12 transform transition-all duration-700 ease-out ${
            animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          {plans.filter(plan => plan.isPurchasable === undefined || plan.isPurchasable).map((plan, index) => (
            <div 
              key={plan._id} 
              className="transform transition-all duration-500"
              style={{ 
                transitionDelay: `${index * 0.2}s`
              }}
            >
              <PricingCard 
                id={plan.planType}
                name={plan.name}
                price={plan.currentPrice} 
                originalPrice={plan.discountPercentage > 0 ? plan.price : undefined}
                duration={plan.planType === 'monthly' ? 'month' : plan.planType === 'yearly' ? 'year' : '3 months'}
                features={formatPlanFeatures(plan)}
                highlight={plan.planType === 'quarterly'}
                minutes={plan.interviewMinutes}
                isUnlimited={plan.isUnlimited}
                hasDiscount={plan.discountPercentage > 0}
                discountPercentage={plan.discountPercentage}
                hasCouponApplied={plan.discountPercentage > 0}
                shouldShowPricing={shouldShowPricing}
                userLocation={userLocation}
                onChoosePlan={() => {
                  if (shouldShowPricing) {
                    trackButtonClick(`Choose ${plan.name} Plan`);
                    window.open(`${import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL}/signup?utm_source=landing_page&utm_medium=pricing_card&utm_campaign=choose_plan&utm_content=${plan.name.toLowerCase()}`, '_blank');
                  } else {
                    handleTrialRequest(plan._id, plan.name, plan.planType);
                  }
                }}
              />
            </div>
          ))}
        </div>
        {/* Free plan */}
        {plans.length > 0 && (
        <div className={`max-w-7xl mx-auto mb-4 p-4 rounded-xl border border-amber-500/20 dark:border-amber-400/20 bg-amber-500/5 dark:bg-amber-400/5 backdrop-blur-sm
        ${animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <div className={`flex flex-col md:flex-row md:items-center`}>
                <div className={`p-2 mb-2 md:mb-0 rounded-full bg-amber-500/10 dark:bg-amber-400/10 w-10 h-10`}>
                  <AlertCircle className="w-6 h-6 text-amber-500 dark:text-amber-400" />
                </div>
                <div className={`flex-1 mb-4 md:mb-0 sm:ml-4`}>
                  <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Free Plan (Default)
                  </h3>
                  <p className="mt-1 text-text-secondary-light dark:text-text-secondary-dark text-sm max-w-3xl">
                      We've a free plan for all the users that offers <span className="text-text-primary-light dark:text-text-primary-dark">{import.meta.env.VITE_DEFAULT_FREE_MINUTES || 20} minutes of interviews on signup</span>. You can upgrade to any of the paid plans to get more minutes.
                  </p>
                </div>
                <div className="flex justify-start md:justify-end">
                <Button variant="secondary" size="md" className="whitespace-nowrap" 
                  onClick={() => {
                    trackButtonClick('Get 1 Free Interview');
                    // Navigate to subscription page with UTM parameters
                    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
                    window.open(`${platformUrl}/signup?utm_source=landing_page&utm_medium=cta_button&utm_campaign=free_interview`, '_blank');
                  }}>
                  Sign up Now!
                </Button>
              </div>
              </div>
            </div>
        )}
        {/* Add-ons Section */}
        {addOns.length > 0 && (
          <div className="mt-24 mb-16">
            <div className="text-center mb-12">
              <h3 
                className={`text-3xl md:text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-text-primary-light to-primary-light dark:from-primary-dark dark:via-text-primary-dark dark:to-primary-dark transform transition-all duration-500 ease-out ${
                  animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                Power Up Your Practice
              </h3>
              <p 
                className={`text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto transform transition-all duration-500 ease-out ${
                  animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                Enhance your interview preparation with additional practice bundles
              </p>
            </div>

            <div 
              className={`grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl px-2 mx-auto transform transition-all duration-700 ease-out ${
                animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            >
              {addOns.filter(addon => addon.isPurchasable === undefined || addon.isPurchasable).map((addon, index) => (
                <div
                  key={addon._id} 
                  className="transform transition-all duration-500"
                  style={{ 
                    transitionDelay: `${index * 0.2 + 0.3}s`
                  }}
                >
                  <AddOnCard 
                    name={addon.name}
                    price={addon.currentPrice}
                    originalPrice={addon.discountPercentage > 0 ? addon.price : undefined}
                    minutes={addon.interviewMinutes}
                    features={formatAddOnFeatures(addon)}
                    hasCouponApplied={addon.discountPercentage > 0}
                    discountPercentage={addon.discountPercentage > 0 ? addon.discountPercentage : undefined}
                    shouldShowPricing={shouldShowPricing}
                    userLocation={userLocation}
                    onPurchase={() => {
                      if (shouldShowPricing) {
                        trackButtonClick(`Select ${addon.name}`);
                        window.open(`${import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL}/subscriptions?utm_source=landing_page&utm_medium=addon_card&utm_campaign=buy_bundle&utm_content=${addon.name.toLowerCase()}`, '_blank');
                      } else {
                        handleAddonTrialRequest(addon._id, addon.name);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div 
          className={`text-center mt-12 transform transition-all duration-500 ease-out ${
            animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button 
            onClick={handleGetFreeInterview}
            className="btn-primary inline-flex items-center justify-center gap-2 px-12 py-4 text-lg relative overflow-hidden group"
          >
            {/* Button shine effect */}
            <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-out"></span>

            Get 1 Free Interview
            <Sparkles className="w-5 h-5 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
          </button>
        </div>
        
        {/* FAQ Section */}
        <div className="mt-24">
          <PricingFAQ />
        </div>
      </div>
    </section>
  );
}

// Apply the section tracking HOC to the Pricing component
export const Pricing = withSectionTracking(PricingComponent, 'Pricing Section');